import AlertIcon from '@assets/icons/alert.svg';
import EllipsisIcon from '@assets/icons/ellipsis.svg';
import i18n from '@/utils/i18n';
import React, { useCallback, useMemo, useState } from 'react';
import TrashIcon from '@assets/icons/trash-small.svg';
import { ActivityIndicator, Text, TouchableOpacity, View } from 'react-native';
import { APP_MODAL_ROUTES } from '@/navigators/routes';
import { AppModalStackParams } from '@/navigators/types';
import { BaseActionSheet } from '@/components/base-action-sheet';
import { Button } from '@/components/button';
import {
  CartDetailsInput,
  FulfillmentName,
  OfflineOrder_SubmitMutation,
  OfflineOrder_SubmitMutationVariables,
} from '@/types';
import { createStyles } from '@/contexts/theme-context';
import { FONTS, SCREEN_PADDING, TYPOGRAPHY } from '@/styles';
import { formatDate } from '@/utils/date';
import { gql, useMutation } from '@apollo/client';
import { Props as ListItemProps, ListOption } from '@/components/list-item';
import { ModalContainer } from '@/components/modal-container';
import {
  NetworkStatus,
  useNetworkStatus,
} from '@/contexts/network-status-context';
import { OfflineOrderData } from '@/adapters/offline-mode';
import { ORDER_FULFILLMENT_TYPE_TO_TRANSLATION_KEY_MAP } from '@domain/fulfillment-settings/blocs/order-availability';
import { QueuedOfflineOrdersSubtitle } from '@/components/offline-orders/queued-offline-orders-subtitle';
import { QueuedOrdersPlacedActionSheet } from '@/components/queued-orders-placed-action-sheet';
import { RecyclerList } from '@/components/recycler-list';
import { StackNavigationProp } from '@react-navigation/stack';
import { useActionSheetManagerContext } from '@/contexts/action-sheet-manager-context';
import { useCalculateVariantPackPrice } from '@/hooks/use-calculate-variant-pack-price';
import { useNavigation } from '@react-navigation/native';
import { useOfflineModeStyledToasts } from '@/hooks/offline-mode/use-offline-mode-styled-toasts';
import { useOfflineQueuedOrders } from '@/hooks/use-offline-data';
import { useUserContext } from '@/contexts/user-context';

type QueuedOfflineOrdersOption = ListOption<{
  orderData: OfflineOrderData;
  orderUUID: string;
}>;

type Navigation = StackNavigationProp<
  AppModalStackParams,
  APP_MODAL_ROUTES.QueuedOfflineOrders
>;

const SUBMIT_OFFLINE_ORDER_MUTATION = gql`
  mutation OfflineOrder_Submit(
    $restaurantUUID: uuid!
    $supplierUUID: uuid!
    $orderNotes: String
    $orderItems: [UpdateItemInput!]!
    $fulfillmentType: String
    $deliveryDate: String
    $poNumber: String
    $additionalInputValues: [AdditionalInputValueInput!]
  ) {
    submitOfflineOrder(
      restaurant_id: $restaurantUUID
      supplier_id: $supplierUUID
      order_notes: $orderNotes
      order_items: $orderItems
      fulfillment_type: $fulfillmentType
      delivery_date: $deliveryDate
      po_number: $poNumber
      additional_input_values: $additionalInputValues
    ) {
      order_id
    }
  }
`;

export const QueuedOfflineOrdersScreen: React.FC = () => {
  const [styles, theme] = useStyles();
  const navigation = useNavigation<Navigation>();
  const { showErrorToast, showSuccessToast } = useOfflineModeStyledToasts();
  const { networkStatus } = useNetworkStatus();
  const { showActionSheet } = useActionSheetManagerContext();
  const { calculateVariantPackPriceUsingPriceEdit } =
    useCalculateVariantPackPrice();

  const [orderToRemove, setOrderToRemove] =
    useState<QueuedOfflineOrdersOption | null>(null);
  const { queuedOfflineOrdersMap, removeOfflineOrder } =
    useOfflineQueuedOrders();
  const { restaurantRelationships } = useUserContext();

  const [isPlacingOrder, setIsPlacingOrder] = useState(false);
  const [errorsMap, setErrorsMap] = useState<{
    [orderUUID: string]: string;
  }>({});

  const restaurantUUIDToNameMap = restaurantRelationships.reduce<{
    [restaurantUUID: string]: string;
  }>((result, relationship) => {
    result[relationship.uuid] = relationship.name;
    return result;
  }, {});

  const getFullfillmentText = (orderData: OfflineOrderData) => {
    const fulfillmentType = orderData.fulfillmentType
      ? ORDER_FULFILLMENT_TYPE_TO_TRANSLATION_KEY_MAP[
          orderData.fulfillmentType as FulfillmentName
        ]
      : '';
    const fulfillmentDate = orderData.deliveryDate
      ? formatDate(orderData.deliveryDate)
      : '';
    return `${fulfillmentType} ${fulfillmentDate}`;
  };

  const offlineOrdersSectionData: QueuedOfflineOrdersOption[] = useMemo(() => {
    return Object.entries(queuedOfflineOrdersMap ?? {}).map(
      ([orderUUID, orderData]) => {
        const itemsLength = orderData.ordersItems.length;
        const itemsSubtitle =
          itemsLength > 1 ? i18n.t('items') : i18n.t('item');

        return {
          metadata: {
            orderData: orderData,
            orderUUID,
          },
          subtitle: (
            <>
              <QueuedOfflineOrdersSubtitle
                subtitle={`${itemsLength} ${itemsSubtitle} • ${
                  orderData.orderTotalPrice
                } • ${getFullfillmentText(orderData)}`}
              />
              {errorsMap[orderUUID] ? (
                <View style={styles.errorWrapper}>
                  <AlertIcon stroke={theme.colors.negative} width={16} />
                  <Text style={styles.errorText}>{errorsMap[orderUUID]}</Text>
                </View>
              ) : null}
            </>
          ),
          title: restaurantUUIDToNameMap[orderData.restaurantUUID],
          value: orderUUID,
        };
      },
    );
  }, [
    errorsMap,
    queuedOfflineOrdersMap,
    restaurantUUIDToNameMap,
    styles,
    theme,
  ]);

  const listItemProps: Partial<ListItemProps<QueuedOfflineOrdersOption>> =
    useMemo(
      () => ({
        renderRightComponent: (option) => {
          return (
            <TouchableOpacity onPress={() => setOrderToRemove(option)}>
              <EllipsisIcon />
            </TouchableOpacity>
          );
        },
      }),
      [],
    );

  const [submitOfflineOrder] = useMutation<
    OfflineOrder_SubmitMutation,
    OfflineOrder_SubmitMutationVariables
  >(SUBMIT_OFFLINE_ORDER_MUTATION);

  const placeOfflineOrders = useCallback(async () => {
    let numberOrdersFailed = 0;
    let numberOrderSucceeded = 0;

    for (const option of offlineOrdersSectionData) {
      const orderData = option.metadata!.orderData;
      const orderUUID = option.metadata!.orderUUID;
      const {
        restaurantUUID,
        supplierUUID,
        ordersItems,
        variantPacksPriceEditMapAtTimeOfOrder,
      } = orderData;
      try {
        setErrorsMap({});
        setIsPlacingOrder(true);
        await submitOfflineOrder({
          variables: {
            additionalInputValues: orderData.additionalInputValues,
            deliveryDate: orderData.deliveryDate,
            fulfillmentType: orderData.fulfillmentType,
            orderItems: ordersItems.map((orderItem) => {
              const cartDetailsAtTimeOfOrder: CartDetailsInput = {
                quantity:
                  orderItem.order_item_prices?.pack_quantity_at_order || 0,
              };
              const variantPackPriceEdit = variantPacksPriceEditMapAtTimeOfOrder
                ? variantPacksPriceEditMapAtTimeOfOrder[
                    orderItem.variants_pack.uuid
                  ]
                : null;
              if (variantPackPriceEdit) {
                cartDetailsAtTimeOfOrder.pack_price_in_micros =
                  calculateVariantPackPriceUsingPriceEdit(
                    orderItem.variants_pack.uuid,
                    variantPackPriceEdit,
                  );
              }

              return {
                cart_details_at_time_of_order: cartDetailsAtTimeOfOrder,
                variant_pack_id: orderItem.variants_pack.uuid,
              };
            }),
            orderNotes: orderData.orderNotes,
            poNumber: orderData.poNumber,
            restaurantUUID,
            supplierUUID,
          },
        });

        removeOfflineOrder(
          restaurantUUID,
          supplierUUID,
          option.value as string,
        );
        numberOrderSucceeded += 1;
      } catch (e) {
        numberOrdersFailed += 1;

        if (e instanceof Error) {
          setErrorsMap((prev) => ({
            ...prev,
            [orderUUID]: (e as Error).message,
          }));
        }
      } finally {
        setIsPlacingOrder(false);
      }
    }

    if (numberOrdersFailed === 0) {
      showActionSheet(QueuedOrdersPlacedActionSheet);
      navigation.goBack();
    } else {
      if (numberOrderSucceeded > 0) {
        showSuccessToast(
          numberOrderSucceeded > 1
            ? i18n.t('number_orders_placed', {
                count: numberOrderSucceeded,
              })
            : i18n.t('single_order_placed'),
        );
      }
      if (numberOrdersFailed > 0) {
        showErrorToast(
          numberOrdersFailed > 1
            ? i18n.t('number_orders_failed', {
                count: numberOrdersFailed,
              })
            : i18n.t('single_order_failed'),
        );
      }
    }
  }, [
    calculateVariantPackPriceUsingPriceEdit,
    navigation,
    offlineOrdersSectionData,
    removeOfflineOrder,
    showActionSheet,
    showErrorToast,
    showSuccessToast,
    submitOfflineOrder,
  ]);

  const removeOrder = useCallback(() => {
    if (orderToRemove === null) {
      return;
    }
    const orderUUID = orderToRemove.value;
    const restaurantUUID = orderToRemove.metadata!.orderData.restaurantUUID;
    const supplierUUID = orderToRemove.metadata!.orderData.supplierUUID;
    const ordersLeft = offlineOrdersSectionData.length - 1;

    removeOfflineOrder(restaurantUUID, supplierUUID, orderUUID as string);
    setOrderToRemove(null);

    showSuccessToast(
      i18n.t('restaurant_order_removed', {
        restaurantName: restaurantUUIDToNameMap[restaurantUUID],
      }),
    );

    if (ordersLeft === 0) {
      navigation.goBack();
    }
  }, [
    navigation,
    offlineOrdersSectionData.length,
    orderToRemove,
    removeOfflineOrder,
    restaurantUUIDToNameMap,
    showSuccessToast,
  ]);

  return (
    <ModalContainer
      navigationHeaderProps={{
        title: i18n.t('pending_orders'),
      }}
    >
      {offlineOrdersSectionData.length === 0 ? (
        <View style={styles.loadingContainer}>
          <ActivityIndicator />
        </View>
      ) : (
        <>
          <RecyclerList
            estimatedItemSize={offlineOrdersSectionData.length}
            listItemProps={listItemProps}
            sections={[
              {
                data: offlineOrdersSectionData,
                key: null,
              },
            ]}
          />
          <Button
            disabled={
              isPlacingOrder || networkStatus !== NetworkStatus.CONNECTED
            }
            onPress={placeOfflineOrders}
            style={styles.placeOrdersButton}
            testID="place-offline-orders-button"
            trackingData={{
              name: 'queued_offline_orders.place_orders_button',
              properties: {
                count: offlineOrdersSectionData.length,
              },
            }}
          >
            {offlineOrdersSectionData.length === 1
              ? i18n.t('place_order')
              : i18n.t('place_orders_count', {
                  count: offlineOrdersSectionData.length,
                })}
          </Button>
        </>
      )}
      <BaseActionSheet
        isVisible={orderToRemove !== null}
        onClose={() => setOrderToRemove(null)}
      >
        <TouchableOpacity
          onPress={removeOrder}
          style={styles.actionSheetWrapper}
        >
          <TrashIcon height={24} stroke={theme.colors.negative} width={24} />
          <Text style={styles.removeOrderText}>{i18n.t('remove_order')}</Text>
        </TouchableOpacity>
      </BaseActionSheet>
    </ModalContainer>
  );
};

const useStyles = createStyles((theme) => ({
  actionSheetWrapper: {
    alignItems: 'center',
    flexDirection: 'row',
    marginBottom: 40,
  },
  errorText: {
    ...TYPOGRAPHY.listSubtitle,
    color: theme.colors.negative,
  },
  errorWrapper: {
    flexDirection: 'row',
    gap: 8,
    marginTop: 4,
  },
  loadingContainer: {
    marginTop: SCREEN_PADDING,
  },
  placeOrdersButton: {
    borderRadius: 8,
    marginBottom: 38,
    marginHorizontal: SCREEN_PADDING,
  },
  removeOrderText: {
    ...FONTS.primarySemiBold,
    color: theme.colors.negative,
    marginLeft: 16,
  },
}));
